import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Plus, Edit2 } from 'lucide';
import { lucideIcon } from '../icon.js';
import { layerTextures } from '../utils/boorprofiel.js';
import {
  ADMIX_KINDS, GRADATIONS, BZB_AMOUNTS, DEFAULT_SAMPLES, soilGroups, bzbGroups, toDrawLayer, soilLabel, bzbLabel, sampleProfileSvg,
} from '../utils/texture-sample.js';
import { bzbFillsLayer, nen6693 } from '@mv/models';
// Dezelfde vormtaal als de lagentabel van een boring (Projecten → boring → Lagen).
import layerStyles from '../styles/recording-row-detail.css?inline';

/**
 * texture-sample-dialog — het voorbeeldprofiel van /settings/textures aanpassen.
 *
 * Links de lagen zoals bij een boring (Projecten → boring → Lagen): een rij per laag met een
 * gekleurde rand; klik je erop, dan klapt het formulier eronder open (één tegelijk), met
 * dezelfde klassen en stijl als daar (styles/recording-row-detail.css). Een BZB krijgt een
 * hoeveelheid; bij 'dominant' of 'volledig' bestaat de laag eruit, zoals in een boring. Rechts het échte boorprofiel
 * (buildBoorprofielSvg) met de texturen zoals ze nu op de pagina staan, ook als die nog niet
 * zijn opgeslagen. Zo zie je meteen wat een kleur of patroon in een echte tekening doet.
 *
 * Tabs zoals bij een boring: Lagen, Afwerking (vulling, bovenafwerking en wat achterblijft, zelfde
 * kaarten als bij een boring), Peilbuis (alleen het filter van–tot: meer tekent het boorprofiel er
 * niet van) en Algemeen (de grondwaterstand).
 *
 * Opent met het voorbeeld dat nu op de pagina staat. 'Toepassen' stuurt `save` met het voorbeeld;
 * de pagina bewaart ze (per browser, zie utils/texture-sample.js).
 */
export class TextureSampleDialog extends LitElement {
  static properties = {
    open: { type: Boolean },
    standard: { type: String },
    sample: { type: Object },     // het voorbeeld zoals het op de pagina staat (zie utils/texture-sample.js)
    textures: { type: Object },   // afwijkingen van het bedrijf, ook niet-opgeslagen
    error: { type: String },      // melding als opslaan mislukte (de pagina zet hem)
    _rows: { state: true },
    _open: { state: true },       // index van de uitgeklapte laag, of -1
    _tab: { state: true },        // 'lagen' | 'afwerking' | 'peilbuis'
    _fillings: { state: true },
    _peilbuis: { state: true },
    _top: { state: true },        // bovenafwerking { type, heightCm } of null
    _left: { state: true },       // achtergelaten in de grond [{ van, tot, type, aard }]
    _gws: { state: true },        // grondwaterstand (cm -mv), '' = geen
  };

  constructor() {
    super();
    this.open = false;
    this.standard = 'nen6693';
    this.sample = null;
    this.textures = {};
    this._rows = [];
    this._open = -1;
    this._tab = 'lagen';
    this._fillings = [];
    this._peilbuis = null;
    this._top = null;
    this._left = [];
    this._gws = '';
  }

  _load(sample) {
    const s = structuredClone(sample || {});
    this._rows = s.layers || [];
    this._fillings = s.fillings || [];
    this._peilbuis = s.peilbuis || null;
    this._top = s.top || null;
    this._left = s.achtergelaten || [];
    this._gws = s.gwsCmMv ?? '';
    this._open = -1;
  }

  willUpdate(changed) {
    // Elke keer dat de modal opengaat: beginnen bij wat er op de pagina staat.
    if (changed.has('open') && this.open) { this._load(this.sample); this._tab = 'lagen'; }
  }

  _close() { this.dispatchEvent(new CustomEvent('close')); }

  _apply() {
    const rows = this._rows
      .map((r) => ({ ...r, from: Number(r.from) || 0, to: Number(r.to) || 0 }))
      .filter((r) => r.to > r.from)
      .sort((a, b) => a.from - b.from);
    const fillings = this._fillings
      .map((f) => ({ ...f, van: Number(f.van) || 0, tot: Number(f.tot) || 0 }))
      .filter((f) => f.tot > f.van && f.materiaal);
    const pb = this._peilbuis;
    const peilbuis = pb && Number(pb.filterBottom) > Number(pb.filterTop)
      ? { filterTop: Number(pb.filterTop) || 0, filterBottom: Number(pb.filterBottom) } : null;
    const achtergelaten = this._left
      .map((o) => ({ ...o, van: Number(o.van) || 0, tot: Number(o.tot) || 0 }))
      .filter((o) => o.tot > o.van);
    const top = this._top?.type ? { type: this._top.type, heightCm: this._top.heightCm === '' ? null : this._top.heightCm } : null;
    const gwsCmMv = this._gws === '' || this._gws == null || !Number.isFinite(Number(this._gws)) ? null : Number(this._gws);
    this.dispatchEvent(new CustomEvent('save', { detail: { sample: { layers: rows, fillings, peilbuis, top, achtergelaten, gwsCmMv } } }));
  }

  _set(i, patch) { this._rows = this._rows.map((r, j) => (j === i ? { ...r, ...patch } : r)); }
  _setItem(i, list, k, patch) {
    const items = this._rows[i][list].map((x, j) => (j === k ? { ...x, ...patch } : x));
    this._set(i, { [list]: items });
  }
  _addItem(i, list, item) { this._set(i, { [list]: [...this._rows[i][list], item] }); }
  _removeItem(i, list, k) { this._set(i, { [list]: this._rows[i][list].filter((_, j) => j !== k) }); }

  _addLayer() {
    const from = this._rows.reduce((max, r) => Math.max(max, Number(r.to) || 0), 0);
    // Zand: in beide normen 'Z', en de meest gangbare laag.
    this._rows = [...this._rows, { from, to: from + 50, soil: 'Z', admix: [], bzb: [] }];
    this._open = this._rows.length - 1; // net als bij een boring: de nieuwe laag staat open
  }

  _removeLayer(i) { this._rows = this._rows.filter((_, j) => j !== i); this._open = -1; }

  _profileSvg() {
    return sampleProfileSvg(
      { layers: this._rows, fillings: this._fillings, peilbuis: this._peilbuis, achtergelaten: this._left, top: this._top, gwsCmMv: this._gws },
      this.standard, this.textures,
    );
  }

  /** "zand, siltig matig" -- zelfde volgorde als de grondsoortkolom van een boring. */
  _soilText(r) {
    const filler = r.bzb.find((b) => b.code && bzbFillsLayer(b.amount));
    const head = filler ? bzbLabel(this.standard, filler.code) : soilLabel(this.standard, r.soil);
    return [head, ...r.admix.filter((a) => a.kind).map((a) => `${a.kind} ${a.grad}`)].filter(Boolean).join(', ') || '-';
  }

  _bzbText(r) {
    return r.bzb.filter((b) => b.code).map((b) => `${b.amount} ${bzbLabel(this.standard, b.code)}`).join(', ') || '-';
  }

  _row(r, i) {
    const active = this._open === i;
    const color = layerTextures(toDrawLayer(r, this.standard), this.standard, this.textures).main.t.fill;
    return html`
      <div class="layer-row ${active ? 'is-active' : ''}" style="border-left:3px solid ${color}; align-items:center;"
        @click=${() => { this._open = active ? -1 : i; }}>
        <span style="font-weight:700; font-family:monospace; color:var(--mv-text-sub);">${r.from}–${r.to}</span>
        <span style="font-weight:500;">${this._soilText(r)}</span>
        <span style="color:var(--mv-text-sub); display:flex; align-items:center; gap:4px;">
          <span style="flex:1; min-width:0;">${this._bzbText(r)}</span>
          <span class="row-edit-hint">${unsafeSVG(lucideIcon(Edit2, 11))}</span>
        </span>
      </div>
      ${active ? this._form(r, i) : ''}`;
  }

  /** Het uitgeklapte formulier: diepte, grondsoort met bijmengingen, bijzondere bestanddelen. */
  _form(r, i) {
    const soils = soilGroups(this.standard);
    const groups = bzbGroups(this.standard);
    const amounts = BZB_AMOUNTS[this.standard] || BZB_AMOUNTS.nen6693;
    const filler = r.bzb.some((b) => bzbFillsLayer(b.amount));
    const del = (title, onClick) => html`<button type="button" class="bzb-del-btn" title=${title} @click=${onClick}>${unsafeSVG(lucideIcon(X, 12))}</button>`;
    return html`
      <div class="layer-expand" @click=${(e) => e.stopPropagation()}>
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
          <div class="expand-field">
            <label>Van (cm)</label>
            <input type="number" min="0" step="10" .value=${String(r.from)} @input=${(e) => this._set(i, { from: e.target.value })}>
          </div>
          <div class="expand-field">
            <label>Tot (cm)</label>
            <input type="number" min="0" step="10" .value=${String(r.to)} @input=${(e) => this._set(i, { to: e.target.value })}>
          </div>
        </div>

        <div class="sectie-kop"><strong>Grondsoort</strong>${filler ? html`<span>De laag bestaat uit een BZB</span>` : ''}</div>
        <div class="expand-field-group zonder-knop-kolom" style="grid-template-columns:1fr; margin-bottom:6px;">
          <div class="expand-field">
            <label>Hoofdbestanddeel</label>
            <select ?disabled=${filler} @change=${(e) => this._set(i, { soil: e.target.value || null })}>
              <option value="">— kies —</option>
              ${soils.map((g) => {
                const opts = g.options.map((o) => html`<option value=${o.code} ?selected=${o.code === r.soil}>${o.label}</option>`);
                return g.label ? html`<optgroup label=${g.label}>${opts}</optgroup>` : opts;
              })}
            </select>
          </div>
        </div>
        ${r.admix.map((a, k) => html`
          <div class="expand-field-group admix-rij" style="margin-bottom:6px;">
            <div class="expand-field">
              <label>${r.admix.length > 1 ? `Bijmenging ${k + 1}` : 'Bijmenging'}</label>
              <select @change=${(e) => this._setItem(i, 'admix', k, { kind: e.target.value })}>
                ${ADMIX_KINDS.map((m) => html`<option value=${m} ?selected=${m === a.kind}>${m}</option>`)}
              </select>
            </div>
            <div class="expand-field">
              <label>Intensiteit</label>
              <select @change=${(e) => this._setItem(i, 'admix', k, { grad: e.target.value })}>
                ${GRADATIONS.map((g) => html`<option value=${g} ?selected=${g === a.grad}>${g}</option>`)}
              </select>
            </div>
            ${del('Bijmenging verwijderen', () => this._removeItem(i, 'admix', k))}
          </div>`)}
        ${r.admix.length < 3 ? html`
          <button type="button" class="bzb-add-btn" @click=${() => this._addItem(i, 'admix', { kind: 'siltig', grad: 'zwak' })}>+ Bijmenging toevoegen</button>` : ''}

        <div class="sectie-kop"><strong>Bijzondere bestanddelen</strong></div>
        <div class="bzb-tegels">
          ${r.bzb.map((b, k) => html`
            <div class="bzb-tegel" style="grid-template-columns:minmax(150px, 2fr) minmax(120px, 1fr) 40px;">
              <div class="expand-field">
                <label>Bestanddeel</label>
                <select @change=${(e) => this._setItem(i, 'bzb', k, { code: e.target.value })}>
                  ${groups.map((g) => html`<optgroup label=${g.label}>
                    ${g.options.map((o) => html`<option value=${o.code} ?selected=${o.code === b.code}>${o.label}</option>`)}
                  </optgroup>`)}
                </select>
              </div>
              <div class="expand-field">
                <label>Hoeveelheid</label>
                <select @change=${(e) => this._setItem(i, 'bzb', k, { amount: e.target.value })}>
                  ${amounts.map((a) => html`<option value=${a} ?selected=${a === b.amount}>${a}</option>`)}
                </select>
              </div>
              ${del('Bestanddeel verwijderen', () => this._removeItem(i, 'bzb', k))}
            </div>`)}
          ${r.bzb.length < 3 ? html`
            <button type="button" class="bzb-add-btn" @click=${() => this._addItem(i, 'bzb', { code: groups[0]?.options[0]?.code, amount: 'weinig' })}>+ Bestanddeel toevoegen</button>` : ''}
        </div>

        <div class="expand-actions">
          <button type="button" class="link-danger" @click=${() => this._removeLayer(i)}>Laag verwijderen</button>
          <span style="flex:1;"></span>
          <button type="button" class="ghost small" @click=${() => { this._open = -1; }}>Klaar</button>
        </div>
      </div>`;
  }

  get _depth() { return this._rows.reduce((m, r) => Math.max(m, Number(r.to) || 0), 0); }

  _setFilling(i, patch) { this._fillings = this._fillings.map((f, j) => (j === i ? { ...f, ...patch } : f)); }

  /** Zelfde kaart als 'Vulling boorgat' bij een boring (recording-row-detail, tab Afwerking). */
  _renderAfwerking() {
    const depth = this._depth;
    const vf = this._fillings.filter((f) => f.van !== '' && f.tot !== '' && Number(f.tot) > Number(f.van))
      .map((f) => ({ van: Number(f.van), tot: Number(f.tot), materiaal: f.materiaal })).sort((a, b) => a.van - b.van);
    const covTop = vf.length ? vf[0].van : null;
    const covBot = vf.length ? Math.max(...vf.map((f) => f.tot)) : null;
    const noGaps = vf.every((f, i) => i === 0 || f.van === vf[i - 1].tot);
    const full = !!(vf.length && depth && covTop === 0 && covBot === depth && noGaps);
    const barDepth = depth || Math.max(1, ...vf.map((f) => f.tot));
    const VUL_KLEUR = { VUL_FG: '#FAECE7', VUL_AG: '#FAECE7', VUL_FZ: '#FAEEDA', VUL_AZ: '#FAEEDA', VUL_BK: '#EAF3DE', VUL_BP: '#EAF3DE', VUL_BS: '#EAF3DE', VUL_KL: '#EAF3DE', VUL_CE: '#B4B2A9', VUL_UG: '#F1EFE8' };
    const vulBg = (code) => (code === 'VUL_OP' ? 'repeating-linear-gradient(45deg, #88878055 0 4px, transparent 4px 8px)' : (VUL_KLEUR[code] || '#E3E1D9'));
    const pill = (text, kind) => html`<span class="pill ${kind}">${text}</span>`;
    return html`
      <div class="tabpane">
        <div class="afw-card">
          <div class="afw-head">
            <span class="afw-title">Vulling boorgat</span>
            <span style="margin-left:auto;">${vf.length ? pill(full ? `Volledig gedekt · ${covTop} tot ${covBot} cm` : 'Gedeeltelijk gedekt', full ? 'ok' : 'warn') : pill('Leeg', 'neutral')}</span>
          </div>
          <div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:10px;">
            <button class="afw-quick-btn" @click=${() => { this._fillings = [{ van: 0, tot: depth || 100, materiaal: 'VUL_UG' }]; }}>Dichten met uitgekomen grond</button>
          </div>
          ${vf.length ? html`
            <div class="afw-bar">
              ${vf.map((f) => html`<div style="left:${(f.van / barDepth) * 100}%; width:${((f.tot - f.van) / barDepth) * 100}%; background:${vulBg(f.materiaal)};"
                title=${`${f.van}–${f.tot} cm · ${nen6693.vulmateriaalLabel(f.materiaal) || 'geen materiaal'}`}></div>`)}
            </div>` : ''}
          ${this._fillings.length ? html`
            <div class="afw-grid">
              <span class="afw-label">Van</span><span class="afw-label">Tot</span><span class="afw-label">Materiaal</span><span></span>
              ${this._fillings.map((f, i) => html`
                <input type="number" .value=${String(f.van ?? '')} placeholder="cm" @input=${(e) => this._setFilling(i, { van: e.target.value })}>
                <input type="number" .value=${String(f.tot ?? '')} placeholder="cm" @input=${(e) => this._setFilling(i, { tot: e.target.value })}>
                <select @change=${(e) => this._setFilling(i, { materiaal: e.target.value })}>
                  <option value="">— kies —</option>
                  ${nen6693.VULMATERIAAL.map((v) => html`<option value=${v.code} ?selected=${v.code === f.materiaal}>${v.label}</option>`)}
                </select>
                <button type="button" class="afw-del" title="Verwijderen" @click=${() => { this._fillings = this._fillings.filter((_, j) => j !== i); }}>×</button>`)}
            </div>` : html`<div class="muted-note">Nog geen vultrajecten.</div>`}
          <button class="bzb-add-btn" style="margin-top:8px;" @click=${() => {
            const van = this._fillings.reduce((m, f) => Math.max(m, Number(f.tot) || 0), 0);
            this._fillings = [...this._fillings, { van, tot: depth > van ? depth : van + 50, materiaal: '' }];
          }}>+ Traject</button>
        </div>

        <div class="afw-card">
          <div class="afw-head">
            <span class="afw-title">Bovenafwerking</span>
            <span style="margin-left:auto;">${this._top?.type ? pill('Ingevuld', 'ok') : this._peilbuis ? pill('Vereist', 'warn') : pill('Optioneel', 'neutral')}</span>
          </div>
          ${this._top ? html`
            <div style="display:grid; grid-template-columns:minmax(0,1fr) 200px; gap:8px; align-items:end;">
              <div><div class="afw-label">Type</div>
                <select class="afw-input" @change=${(e) => { this._top = { ...this._top, type: e.target.value }; }}>
                  <option value="">— kies —</option>
                  ${nen6693.BOVENAFWERKING.map((b) => html`<option value=${b.code} ?selected=${b.code === this._top.type}>${b.label}</option>`)}
                </select></div>
              <div><div class="afw-label">Hoogte t.o.v. maaiveld (cm)</div>
                <input class="afw-input" type="number" .value=${String(this._top.heightCm ?? '')} @input=${(e) => { this._top = { ...this._top, heightCm: e.target.value }; }}></div>
            </div>
            <div class="muted-note" style="margin-top:8px; font-size:0.75rem;">Wordt alleen getekend als je dit type een kleur of patroon geeft (groep Bovenafwerking in de tab Afwerking).</div>
            <button class="afw-quick-btn" style="margin-top:10px;" @click=${() => { this._top = null; }}>Bovenafwerking verwijderen</button>
          ` : html`
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
              <span class="muted-note" style="flex:1; min-width:180px; font-size:0.8rem;">Alleen invullen als er een afdekking op het boorgat ligt.</span>
              <button class="afw-quick-btn" @click=${() => { this._top = { type: '', heightCm: '' }; }}>+ Bovenafwerking</button>
            </div>`}
        </div>

        <div class="afw-card">
          <div class="afw-head">
            <span class="afw-title">Achtergelaten in de grond</span>
            <span style="margin-left:auto;"><button class="afw-quick-btn" @click=${() => {
              const d = this._depth || 200;
              this._left = [...this._left, { van: Math.max(0, d - 100), tot: d, type: 'CAS', aard: 'achtergebleven' }];
            }}>+ Object</button></span>
          </div>
          <div class="muted-note" style="font-size:0.72rem; margin:-6px 0 8px;">Zelden nodig · objecten die in de bodem blijven</div>
          ${this._left.map((o, i) => this._renderLeftBehind(o, i))}
        </div>
      </div>`;
  }

  _setLeft(i, patch) { this._left = this._left.map((o, j) => (j === i ? { ...o, ...patch } : o)); }

  /** Zelfde opbouw als bij een boring (_renderAchtergelatenRow): aard, van/tot en type. */
  _renderLeftBehind(o, i) {
    const aard = (val, label, color) => {
      const on = o.aard === val;
      return html`<button type="button" class="aard ${on ? 'on' : ''}" style=${on ? `background:${color}; border-color:${color};` : ''}
        @click=${() => this._setLeft(i, { aard: on ? null : val })}>${label}</button>`;
    };
    return html`
      <div class="left-row">
        <div style="display:flex; gap:6px; align-items:center;">
          ${aard('geplaatst', 'Bewust geplaatst', 'var(--mv-primary)')}
          ${aard('achtergebleven', 'Onbedoeld achter', '#D97706')}
          <button type="button" class="afw-del" title="Verwijderen" @click=${() => { this._left = this._left.filter((_, j) => j !== i); }}>×</button>
        </div>
        <div style="display:grid; grid-template-columns:72px 72px minmax(0,1fr); gap:8px; align-items:end;">
          <div><div class="afw-label">Van</div><input class="afw-input" type="number" .value=${String(o.van ?? '')} @input=${(e) => this._setLeft(i, { van: e.target.value })}></div>
          <div><div class="afw-label">Tot</div><input class="afw-input" type="number" .value=${String(o.tot ?? '')} @input=${(e) => this._setLeft(i, { tot: e.target.value })}></div>
          <div><div class="afw-label">Type</div>
            <select class="afw-input" @change=${(e) => this._setLeft(i, { type: e.target.value })}>
              <option value="">— kies —</option>
              ${nen6693.ACHTERGELATEN_TYPE.map((t) => html`<option value=${t.code} ?selected=${t.code === o.type}>${t.label}</option>`)}
            </select></div>
        </div>
      </div>`;
  }

  /** Peilbuis: alleen het filter. Blind stuk = maaiveld tot de bovenkant van het filter. */
  _renderPeilbuis() {
    const pb = this._peilbuis;
    return html`
      <div class="tabpane">
        ${pb ? html`
          <div class="afw-card">
            <div class="afw-head"><span class="afw-title">Peilbuis</span></div>
            <div class="expand-field-group" style="grid-template-columns:1fr 1fr 40px; align-items:end;">
              <div class="expand-field">
                <label>Filter van (cm -mv)</label>
                <input type="number" min="0" .value=${String(pb.filterTop ?? '')} @input=${(e) => { this._peilbuis = { ...pb, filterTop: e.target.value }; }}>
              </div>
              <div class="expand-field">
                <label>Filter tot (cm -mv)</label>
                <input type="number" min="0" .value=${String(pb.filterBottom ?? '')} @input=${(e) => { this._peilbuis = { ...pb, filterBottom: e.target.value }; }}>
              </div>
              <button type="button" class="bzb-del-btn" title="Peilbuis verwijderen" @click=${() => { this._peilbuis = null; }}>${unsafeSVG(lucideIcon(X, 12))}</button>
            </div>
          </div>` : html`
          <div class="muted-note" style="margin-bottom:8px;">Geen peilbuis in dit voorbeeld.</div>
          <button class="bzb-add-btn" @click=${() => {
            const d = this._depth || 200;
            this._peilbuis = { filterTop: Math.max(0, d - 70), filterBottom: Math.max(10, d - 10) };
          }}>+ Peilbuis</button>`}
      </div>`;
  }

  /** Algemeen: de grondwaterstand, als lijn in het profiel (kleur uit de tab Peilbuis). */
  _renderAlgemeen() {
    return html`
      <div class="tabpane">
        <div class="afw-card">
          <div class="afw-head"><span class="afw-title">Grondwater</span></div>
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr; align-items:end;">
            <div class="expand-field">
              <label>Grondwaterstand (cm -mv)</label>
              <input type="number" min="0" placeholder="geen grondwater" .value=${String(this._gws ?? '')}
                @input=${(e) => { this._gws = e.target.value; }}>
            </div>
            <div>${this._gws !== '' && this._gws != null
              ? html`<button class="afw-quick-btn" @click=${() => { this._gws = ''; }}>Geen grondwater</button>` : ''}</div>
          </div>
          <div class="muted-note" style="margin-top:8px; font-size:0.75rem;">Getekend als lijn met label GWS, in de kleur van 'Grondwaterstand (lijn)' in de tab Peilbuis.</div>
        </div>
      </div>`;
  }

  render() {
    if (!this.open) return html``;
    const std = this.standard === 'nen5104' ? 'NEN 5104' : 'NEN 6693';
    return html`
      <div class="back" @click=${(e) => { if (e.target === e.currentTarget) this._close(); }}>
        <div class="modal" role="dialog" aria-label="Voorbeeld aanpassen">
          <div class="head">
            <div>
              <h3>Voorbeeld aanpassen</h3>
              <div class="sub">Lagen in ${std}. Alleen voor dit voorbeeld; je boringen veranderen niet.</div>
            </div>
            <button class="x" @click=${this._close} aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 18))}</button>
          </div>

          <div class="body">
            <section class="layers">
              <div class="tabbar">
                ${[
                  { id: 'lagen', label: 'Lagen', count: this._rows.length },
                  { id: 'afwerking', label: 'Afwerking', count: this._fillings.length + this._left.length + (this._top ? 1 : 0) },
                  { id: 'peilbuis', label: 'Peilbuis', count: this._peilbuis ? 1 : 0 },
                  { id: 'algemeen', label: 'Algemeen', count: null },
                ].map((t) => html`
                  <button class="tabbtn ${this._tab === t.id ? 'on' : ''}" @click=${() => { this._tab = t.id; }}>
                    ${t.label}${t.count != null ? html`<span class="tabcount">${t.count}</span>` : ''}
                  </button>`)}
              </div>
              ${this._tab === 'afwerking' ? this._renderAfwerking()
                : this._tab === 'peilbuis' ? this._renderPeilbuis()
                : this._tab === 'algemeen' ? this._renderAlgemeen()
                : html`
                  <div class="layer-header"><span>Diepte (cm)</span><span>Grondsoort</span><span>Bijzonderheden</span></div>
                  ${this._rows.map((r, i) => this._row(r, i))}
                  ${!this._rows.length ? html`<div class="empty">Nog geen lagen.</div>` : ''}
                  <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">
                    <button class="bzb-add-btn" @click=${this._addLayer}>${unsafeSVG(lucideIcon(Plus, 12))} Laag toevoegen</button>
                  </div>`}
            </section>
            <aside class="profile">${unsafeSVG(this._profileSvg())}</aside>
          </div>

          <div class="foot">
            <button class="ghost" @click=${() => this._load(DEFAULT_SAMPLES[this.standard])}>Standaardvoorbeeld</button>
            <span class="grow"></span>
            ${this.error ? html`<span class="save-err">${this.error}</span>` : ''}
            <button class="ghost" @click=${this._close}>Annuleren</button>
            <button class="primary" @click=${this._apply}>Toepassen</button>
          </div>
        </div>
      </div>`;
  }

  static styles = [unsafeCSS(layerStyles), css`
    :host { display: contents; }
    .back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 4vh 1rem; }
    .modal { width: min(1280px, 100%); max-height: 92vh; background: var(--mv-surface); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; }
    .head { display: flex; align-items: flex-start; gap: 12px; padding: 22px 24px 14px; border-bottom: 1px solid var(--mv-border); }
    .head h3 { margin: 0; font-size: 1rem; font-weight: 800; color: var(--mv-text); }
    .sub { font-size: .8rem; color: var(--mv-text-muted); margin-top: 2px; }
    .x { margin-left: auto; display: inline-flex; background: none; border: none; cursor: pointer; color: var(--mv-text-sub); padding: 4px; }

    .body { display: flex; gap: 20px; padding: 16px 24px; overflow: auto; align-items: flex-start; }
    /* De kaart van een boring: rand, ronde hoeken, en de lagentabel met drie kolommen. */
    .layers { flex: 1 1 560px; min-width: 0; border: 1px solid var(--mv-border); border-radius: 8px; overflow: hidden; background: var(--mv-surface); }
    .layers .layer-header, .layers .layer-row { grid-template-columns: 80px minmax(0, 1.4fr) minmax(0, 1fr); }
    .layers .bzb-add-btn { display: inline-flex; align-items: center; justify-content: center; gap: 4px; }
    /* Tabbalk zoals bij een boring. */
    .tabbar { display: flex; border-bottom: 1px solid var(--mv-border); background: var(--mv-surface); }
    .tabbtn { background: none; border: none; border-bottom: 2px solid transparent; padding: 6px 12px; font: inherit;
      font-size: 0.75rem; font-weight: 700; cursor: pointer; color: var(--mv-text-muted); display: flex; align-items: center; gap: 4px; }
    .tabbtn.on { color: var(--mv-primary); border-bottom-color: var(--mv-primary); }
    .tabcount { background: var(--mv-surface-alt); color: var(--mv-text-muted); font-size: 0.65rem; font-weight: 700;
      border-radius: 99px; padding: 0 5px; min-width: 16px; text-align: center; }
    .tabpane { padding: 0.7rem 0.9rem; display: flex; flex-direction: column; gap: 12px; }
    .afw-card { border: 1px solid var(--mv-border); border-radius: 10px; padding: 14px 16px; background: var(--mv-surface-alt, #F9FAFB); }
    .afw-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .afw-title { font-weight: 700; font-size: 0.88rem; color: var(--mv-text); }
    .pill { font-size: 0.7rem; font-weight: 700; padding: 2px 9px; border-radius: 99px; background: var(--mv-surface-alt); color: var(--mv-text-muted); }
    .pill.ok { background: var(--mv-primary-tint, #e9f5e6); color: var(--mv-primary); }
    .pill.warn { background: var(--mv-warning-panel-bg, #FEF3C7); color: #B45309; }
    .afw-quick-btn { padding: 4px 10px; border: 1px solid var(--mv-border); border-radius: 6px; background: var(--mv-surface);
      font: inherit; font-size: 0.75rem; font-weight: 600; color: var(--mv-text); cursor: pointer; }
    .afw-quick-btn:hover { border-color: var(--mv-primary); color: var(--mv-primary); }
    .afw-bar { position: relative; height: 8px; border-radius: 4px; background: var(--mv-surface-alt, #F3F4F6); overflow: hidden; margin-bottom: 10px; }
    .afw-bar > div { position: absolute; top: 0; bottom: 0; }
    .afw-grid { display: grid; grid-template-columns: 72px 72px minmax(0, 1fr) 26px; gap: 6px 8px; align-items: center; }
    .afw-grid input, .afw-grid select { padding: 5px 8px; border: 1px solid var(--mv-border); border-radius: 5px; font: inherit;
      font-size: 0.82rem; background: var(--mv-surface); color: var(--mv-text); min-width: 0; box-sizing: border-box; }
    .afw-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--mv-text-muted); }
    .afw-del { border: none; background: none; cursor: pointer; color: var(--mv-danger, #f1416c); font-size: 1rem; line-height: 1; padding: 0 4px; }
    .muted-note { font-size: 0.82rem; color: var(--mv-text-muted); }
    .afw-input { width: 100%; padding: 5px 8px; border: 1px solid var(--mv-border); border-radius: 5px; font: inherit;
      font-size: 0.82rem; background: var(--mv-surface); color: var(--mv-text); box-sizing: border-box; }
    .left-row { border: 1px solid var(--mv-border); border-radius: 8px; padding: 10px; margin-bottom: 8px; display: flex; flex-direction: column; gap: 8px; }
    .aard { flex: 1; padding: 5px 8px; border-radius: 5px; border: 1px solid var(--mv-border); background: var(--mv-surface);
      color: var(--mv-text); font: inherit; font-weight: 700; font-size: 0.8rem; cursor: pointer; }
    .aard.on { color: #fff; }
    .empty { padding: 0.75rem 0.9rem; font-size: 0.85rem; color: var(--mv-text-muted); }
    .link-danger { border: none; background: none; padding: 0; font: inherit; font-size: .8rem; font-weight: 600; color: #F1416C; cursor: pointer; }
    .link-danger:hover { text-decoration: underline; }

    /* Het profiel op ware grootte (zonder legenda ~430 px breed) en zonder eigen kader: de
       tekening heeft zelf al een kop met de naam. */
    .profile { flex: 0 1 440px; min-width: 300px; }
    .profile svg { display: block; }

    .foot { display: flex; align-items: center; gap: 10px; padding: 14px 24px; border-top: 1px solid var(--mv-border); }
    .grow { flex: 1; }
    .save-err { color: #DC2626; font-weight: 600; font-size: .82rem; }
    button.ghost { padding: 8px 14px; border: 1px solid var(--mv-border); border-radius: 8px; background: var(--mv-surface);
      font: inherit; font-size: .84rem; font-weight: 600; color: var(--mv-text); cursor: pointer; }
    button.ghost.small { padding: 5px 14px; font-size: .8rem; }
    button.ghost:hover { border-color: var(--mv-text-sub); }
    button.primary { padding: 8px 18px; border: none; border-radius: 8px; background: var(--mv-primary); color: #fff;
      font: inherit; font-size: .84rem; font-weight: 700; cursor: pointer; }
    @media (max-width: 900px) { .body { flex-direction: column; } .profile { position: static; width: 100%; } }
  `];
}

customElements.define('texture-sample-dialog', TextureSampleDialog);
